import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, CheckCircle2, AlertCircle, ShieldCheck } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import PreLaunchBanner from "@/components/redeem/PreLaunchBanner";
import { postNativeConsume } from "@/lib/api";

/**
 * AP-5B.2 — `/signin/consume` — atomic single-use consumer page.
 *
 * The user arrives here from a magic link with `?token=...`. We:
 *   1. Read the token + optional `return_url` from the query string.
 *   2. IMMEDIATELY strip both via `history.replaceState` — the URL
 *      must not retain a single-use secret in the address bar / history.
 *   3. POST to `/api/auth/native/consume`.
 *   4. On success, redirect to the sanitised return URL (default `/account`).
 *   5. On 400/409, render a clear error with a "Send a new link" CTA.
 *
 * Strict rules:
 *   - NEVER print the raw token to the UI, console, or analytics.
 *   - NEVER store the token in localStorage / sessionStorage.
 *   - NEVER auto-retry on failure (single-use tokens cannot be replayed).
 *   - Same return-url sanitiser the backend uses (defence in depth).
 */

const REDIRECT_DELAY_MS = 1400;
const DEFAULT_RETURN_URL = "/account";

function safeReturnUrl(raw) {
  if (typeof raw !== "string") return DEFAULT_RETURN_URL;
  if (!raw.startsWith("/")) return DEFAULT_RETURN_URL;
  if (raw.startsWith("//")) return DEFAULT_RETURN_URL;
  if (raw.split("?")[0].includes(":")) return DEFAULT_RETURN_URL;
  return raw;
}

function mapError(err) {
  const status = err?.response?.status;
  const code = err?.response?.data?.detail?.error;
  if (status === 409 || code === "native_replayed") {
    return {
      title: "This link has already been used",
      detail:
        "Each magic link is one-time-use. Request a fresh link from the sign-in page to continue.",
      kind: "replayed",
    };
  }
  if (code === "native_expired") {
    return {
      title: "This link has expired",
      detail:
        "Magic links are valid for 15 minutes. Request a fresh link to continue.",
      kind: "expired",
    };
  }
  if (status === 400 || status === 422 || code === "native_invalid") {
    return {
      title: "We couldn't verify this link",
      detail:
        "The link could not be verified. Request a fresh one from the sign-in page.",
      kind: "invalid",
    };
  }
  return {
    title: "We couldn't reach AssosPay",
    detail:
      "Something interrupted link verification. Please try again in a moment.",
    kind: "network",
  };
}

export default function SigninConsume() {
  const [state, setState] = useState({ kind: "loading", error: null });
  const navigate = useNavigate();
  const fired = useRef(false);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (fired.current) return;
    fired.current = true;

    const params = new URLSearchParams(window.location.search);
    const token = params.get("token");
    const requestedReturn = safeReturnUrl(params.get("return_url"));

    // Strip the token from the URL IMMEDIATELY, before any await.
    try {
      window.history.replaceState(null, "", "/signin/consume");
    } catch (_) {
      // Older browsers — proceed anyway.
    }

    if (!token) {
      setState({ kind: "missing_token", error: null });
      return;
    }

    let redirectTimer = null;
    (async () => {
      try {
        const out = await postNativeConsume(token);
        if (!isMountedRef.current) return;
        const target = safeReturnUrl(out?.return_url) || requestedReturn;
        setState({ kind: "success", error: null, target });
        redirectTimer = setTimeout(() => {
          if (isMountedRef.current) navigate(target, { replace: true });
        }, REDIRECT_DELAY_MS);
      } catch (e) {
        if (!isMountedRef.current) return;
        setState({ kind: "error", error: mapError(e) });
      }
    })();

    return () => {
      if (redirectTimer) clearTimeout(redirectTimer);
    };
  }, [navigate]);

  return (
    <div
      className="min-h-screen bg-[#050505] text-white"
      data-testid="signin-consume-page"
    >
      <Header />
      <div className="relative">
        <div className="spotlight absolute inset-0 -z-0 pointer-events-none" />
        <main className="relative z-10 max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
          <div className="mb-6">
            <PreLaunchBanner />
          </div>
          <div
            className="glass rounded-md p-8"
            data-signin-consume-state={state.kind}
          >
            {state.kind === "loading" && <LoadingView />}
            {state.kind === "success" && <SuccessView target={state.target} />}
            {state.kind === "missing_token" && <MissingTokenView />}
            {state.kind === "error" && <ErrorView error={state.error} />}
          </div>

          <div
            className="mt-6 flex items-center gap-2 text-white/45"
            data-testid="signin-consume-security-strip"
          >
            <ShieldCheck className="h-3.5 w-3.5 text-[#00F0FF]" />
            <span className="font-mono text-[11px]">
              No password · No wallet signature · One-time link only
            </span>
          </div>
        </main>
      </div>
      <Footer />
    </div>
  );
}

function LoadingView() {
  return (
    <div
      data-testid="signin-consume-loading"
      className="flex flex-col items-start gap-3"
    >
      <Loader2 className="h-6 w-6 animate-spin text-[#00F0FF]" />
      <h1 className="font-display text-xl font-black uppercase tracking-[0.2em] text-white">
        Verifying your link…
      </h1>
      <p className="font-mono text-xs text-white/55">
        We are validating your one-time magic link. This usually takes
        less than a second.
      </p>
    </div>
  );
}

function SuccessView({ target }) {
  return (
    <div
      data-testid="signin-consume-success"
      className="flex flex-col items-start gap-4"
    >
      <div className="flex items-center gap-2 text-[#39FF14]">
        <CheckCircle2 className="h-6 w-6" />
        <span className="font-display text-xl font-black uppercase tracking-[0.2em] glow-green">
          Signed in
        </span>
      </div>
      <p className="font-mono text-xs text-white/70">
        Your AssosPay session is now active. Redirecting you to{" "}
        <span className="text-white/90">{target}</span>…
      </p>
      <Link
        to={target}
        replace
        className="btn-primary text-xs"
        data-testid="signin-consume-continue-btn"
      >
        Continue now
      </Link>
    </div>
  );
}

function MissingTokenView() {
  return (
    <div
      data-testid="signin-consume-missing-token"
      className="flex flex-col items-start gap-4"
    >
      <div className="flex items-center gap-2 text-[#FFB800]">
        <AlertCircle className="h-6 w-6" />
        <span className="font-display text-xl font-black uppercase tracking-[0.2em]">
          No link token
        </span>
      </div>
      <p className="font-mono text-xs text-white/65">
        This page expects a one-time magic link. Start the sign-in flow
        from{" "}
        <Link
          to="/signin"
          className="text-[#00F0FF] hover:text-[#39FF14] transition-colors"
        >
          /signin
        </Link>{" "}
        to receive a fresh link.
      </p>
      <Link
        to="/signin"
        className="btn-secondary text-xs"
        data-testid="signin-consume-back-btn"
      >
        Go to sign in
      </Link>
    </div>
  );
}

function ErrorView({ error }) {
  return (
    <div
      data-testid="signin-consume-error"
      data-error-kind={error.kind}
      className="flex flex-col items-start gap-4"
    >
      <div className="flex items-center gap-2 text-[#FF3B30]">
        <AlertCircle className="h-6 w-6 glow-red" />
        <span className="font-display text-xl font-black uppercase tracking-[0.2em]">
          Sign-in failed
        </span>
      </div>
      <h2
        className="font-display text-base text-white"
        data-testid="signin-consume-error-title"
      >
        {error.title}
      </h2>
      <p
        className="font-mono text-xs text-white/65"
        data-testid="signin-consume-error-detail"
      >
        {error.detail}
      </p>
      <div className="mt-2 flex items-center gap-3">
        <Link
          to="/signin"
          className="btn-secondary text-xs"
          data-testid="signin-consume-error-back"
        >
          Request a new link
        </Link>
      </div>
      <p className="mt-2 font-mono text-[10px] text-white/40">
        For your security, magic links are single-use and short-lived.
      </p>
    </div>
  );
}
